'use client';

import {
  useApolloClient,
  useReactiveVar,
  useSuspenseQuery,
} from '@apollo/client';
import currency from 'currency.js';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import ThemeProviderWrapperV2 from '@storefront/app/ThemeProviderWrapperV2';
import { BundleProductWithLowestPrice } from '@storefront/app/v2/pdp-config/printer/product-configuration/product-configuration.utils';
import { configurationState } from '@storefront/app/v2/pdp-config/printer/state-management/pdp-config-state';
import { getSkuList } from '@storefront/app/v2/pdp-config/printer/utils/utils';
import {
  GetPdpStateDocument,
  GetPdpStateQuery,
  GetSkuProductsDocument,
  GetSkuProductsQuery,
} from '@storefront/data-commerce';
import { NamespacesI18n } from '@storefront/i18n';
import { getCurrencySymbol } from '@storefront/utils/bundlePriceCalculator';
import { getProductsLowestPrice } from '@storefront/utils/utils';

import styles from './order-details-scroll-bar.module.scss';
import { ContinueButton } from '../continue-button/continue-button';

interface OrderDetailsScrollBarProps {
  isContinueButtonDisabled: boolean;
  loading: boolean;
  isLoading: (loading: boolean) => void;
  hasTriggeredEvent: boolean;
  setHasTriggeredEvent: (value: boolean) => void;
  currencyCode?: string;
  isBelow?: boolean;
}

export default function OrderDetailsScrollBar({
  isContinueButtonDisabled,
  loading,
  isLoading,
  hasTriggeredEvent,
  setHasTriggeredEvent,
  isBelow,
}: Readonly<OrderDetailsScrollBarProps>) {
  const { t } = useTranslation(NamespacesI18n.PDP_CONFIG);
  const configuration = useReactiveVar(configurationState);
  const skuList = getSkuList(useSearchParams()?.get('skus'));
  const client = useApolloClient();

  const {
    pdpConfig: { prices, selected_options },
  } = client.readQuery<GetPdpStateQuery>({
    query: GetPdpStateDocument,
    variables: { skuList, configuration },
  });

  const {
    data: { products },
  } = useSuspenseQuery<GetSkuProductsQuery>(GetSkuProductsDocument, {
    variables: { skuList },
  });

  const currencyFromProducts =
    products?.items?.[0]?.price_range?.minimum_price?.regular_price?.currency ||
    'USD';

  const handlePrice = () => {
    if (selected_options?.length > 0) {
      return currency(prices?.totalPricePerMonth, {
        pattern: '!#',
        symbol: getCurrencySymbol(currencyFromProducts),
        precision: 2,
      }).format();
    } else {
      return currency(
        getProductsLowestPrice(
          products?.items as BundleProductWithLowestPrice[]
        ),
        {
          pattern: '!#',
          symbol: getCurrencySymbol(currencyFromProducts),
          precision: 2,
        }
      ).format();
    }
  };

  return (
    <div
      className={`${isBelow ? styles['below-container'] : styles['container']}`}
    >
      <div>
        <div className={styles['price-continue']}>
          <div className={styles['price']}>
            <span className={`body-large`}>
              {isContinueButtonDisabled &&
                t('pdpConfigFooterWithPrice.priceRange')}
              {!isContinueButtonDisabled &&
                t('pdpConfigFooterWithPrice.priceTotal')}
            </span>
            <span className={`body-large`} data-testid="total-price">
              {t('pdpConfigFooterWithPrice.price', {
                price: handlePrice(),
              })}
            </span>
          </div>
          <div className={styles['continue-button']}>
            <ThemeProviderWrapperV2>
              <ContinueButton
                isDisabled={isContinueButtonDisabled}
                loading={loading}
                isLoading={isLoading}
                hasTriggeredEvent={hasTriggeredEvent}
                setHasTriggeredEvent={setHasTriggeredEvent}
              />
            </ThemeProviderWrapperV2>
          </div>
        </div>
      </div>
    </div>
  );
}
